import React, { useContext, useState } from 'react'
import {
  Button,
  CenteredSpinner,
  Col,
  Form,
  Row,
  DocusignAuthRequest,
  useToaster
} from 'react-angle-dashboard-components'
import { docusignService, groupService } from '../../../../services'
import { useHistory } from 'react-router-dom'
import { BusinessOnboardingContext } from '../../context/BusinessOnboardingContext'
import { useStep } from '../../hooks/use-step'
import { IBusinessOnboardingDTO } from '../../interfaces'
import { businessOnboardingPaths } from '../../router/paths'
import { FooterActions } from '../FooterActions'
import { TitleRow } from '../style'
import { AgreementContainer } from './style'
import { useQuery, useMutation } from 'react-query'
import { agreementText, renewalAgreementText } from './agreementText'
import { system } from '@angle/utils'
import useOnboardingDocusignComplete from '../../hooks/useOnboardingDocusignComplete'

const mapDTOtoStatement = (data: IBusinessOnboardingDTO) => {
  return {
    stepNumber: data.in_progress_state,
    isRenewal: data.in_renewal
  }
}

export const Statement: React.FC = () => {
  const history = useHistory()

  const { showError } = useToaster()

  const systemInfo = system.getInfo()

  const { state, setState } = useContext(BusinessOnboardingContext)

  const [agree, setAgree] = useState(false)

  const {
    signingComplete,
    data: docusignData,
    isLoading: isLoadingDocusign,
    isError: isDocusignError
  } = useOnboardingDocusignComplete()

  const { isLoading, data: groupData } = useQuery(
    'getStatement',
    () => groupService.getInfo(mapDTOtoStatement, true),
    {
      refetchOnWindowFocus: false,
      cacheTime: 0,
      onSuccess: (data: any) => {
        setState(data.stepNumber)
      }
    }
  )

  const signAgreement = useMutation(
    (data: DocusignAuthRequest) => docusignService.docusignAuth(data),
    {
      onError: () => {
        showError(
          'An error occurred signing the agreement. Please reach out if the issue continues.',
          false
        )
        setAgree(false)
      }
    }
  )

  const handleBack = () => {
    history.push(businessOnboardingPaths.producer)
  }

  useStep(state, 9, 'statement', 9)

  return (
    <>
      {isLoading ? (
        <CenteredSpinner stle={{ height: '40vh' }} />
      ) : (
        <div>
          <TitleRow noGutters>
            <b>Statement of Understanding</b>
          </TitleRow>

          <AgreementContainer>
            {!isLoading && groupData?.isRenewal
              ? renewalAgreementText
              : agreementText}
          </AgreementContainer>
          <Form.Group>
            <Form.Check
              className="custom-checkbox"
              id="agree"
              type="checkbox"
              label="I certify that I have read the above statements. The answers to all questions are complete and accurate, to the best of my knowledge."
              checked={agree}
              onChange={async (e) => {
                const newCheckedValue = e.target.checked
                if (newCheckedValue) {
                  await signAgreement.mutateAsync({
                    text: agreementText,
                    device_info: `${systemInfo.os.name} ${systemInfo.os.version}`,
                    client_info: `${systemInfo.browser.name}/${systemInfo.browser.version}`,
                    time: new Date().toISOString()
                  })
                }
                setAgree(newCheckedValue)
              }}
            />
          </Form.Group>
          {!signingComplete && (
            <Row>
              <Col className="pb-5 d-flex flex-column align-items-center">
                <Button
                  style={{
                    background: '#ffce00',
                    border: 'none',
                    borderRadius: 5,
                    color: 'black'
                  }}
                  href={docusignData?.redirect_url}
                  disabled={
                    isLoadingDocusign ||
                    isDocusignError ||
                    !docusignData?.redirect_url ||
                    !agree
                  }
                >
                  Sign with <span style={{ fontWeight: 'bold' }}>DocuSign</span>
                </Button>
                {isDocusignError && (
                  <div className="text-danger font-weight-bold py-3">
                    An error occurred connecting to Docusign. Please reach out
                    if the issue continues.
                  </div>
                )}
              </Col>
            </Row>
          )}
        </div>
      )}

      <FooterActions backHandler={handleBack} showContinue={false} />
    </>
  )
}
